<script>
import { computed, ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { compareLabelsByLength } from 'dashboard/helper/labelOrder';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import NextLabel from 'dashboard/components-next/label/Label.vue';
import AddLabelForm from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';

export default {
  components: {
    Spinner,
    LabelDropdown,
    NextLabel,
    AddLabelForm,
  },
  setup() {
    const { isAdmin } = useAdmin();

    const {
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    // Mesma ordenação dos cartões da lista de conversas: da etiqueta mais curta
    // para a mais longa, com desempate alfabético.
    const sortedActiveLabels = computed(() =>
      [...activeLabels.value].sort(compareLabelsByLength)
    );

    const showSearchDropdownLabel = ref(false);
    const showCreateLabelModal = ref(false);
    const newLabelTitle = ref('');

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const openCreateLabelModal = prefillTitle => {
      newLabelTitle.value = prefillTitle;
      closeDropdownLabel();
      showCreateLabelModal.value = true;
    };

    const closeCreateLabelModal = () => {
      showCreateLabelModal.value = false;
      newLabelTitle.value = '';
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      savedLabels,
      activeLabels,
      sortedActiveLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      showSearchDropdownLabel,
      showCreateLabelModal,
      newLabelTitle,
      closeDropdownLabel,
      openCreateLabelModal,
      closeCreateLabelModal,
      toggleLabels,
    };
  },
  data() {
    return {
      selectedLabels: [],
    };
  },

  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
  },
};
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!conversationUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap items-center gap-1.5"
        @keyup.esc="closeDropdownLabel"
      >
        <!-- Mesma etiqueta dos cartões da lista: borda esquerda na cor, fundo
             levemente tingido e texto compacto. -->
        <NextLabel
          v-for="label in sortedActiveLabels"
          :key="label.id"
          :label="label"
          color-border
          compact
          class="max-w-full"
        >
          <template #action>
            <button
              type="button"
              class="flex-shrink-0 !p-0 leading-none text-n-slate-10 transition hover:text-n-slate-12"
              :aria-label="
                $t('CONTACT_PANEL.LABELS.CONVERSATION.REMOVE', {
                  label: label.title,
                })
              "
              @click="removeLabelFromConversation(label.title)"
            >
              <span class="i-hugeicons-cancel-01 size-3" />
            </button>
          </template>
        </NextLabel>

        <!-- A ação de incluir fica no fim da fila, como um "+" discreto; sem etiquetas,
             mostra também o texto para não parecer um quadrado vazio. -->
        <button
          v-tooltip="$t('CONTACT_PANEL.LABELS.CONVERSATION.ADD_BUTTON')"
          type="button"
          class="inline-flex h-6 flex-shrink-0 items-center justify-center gap-1 rounded-md border border-n-strong text-xs font-medium text-n-slate-12 transition hover:bg-n-alpha-2"
          :class="sortedActiveLabels.length ? 'w-6' : 'px-2'"
          :aria-label="$t('CONTACT_PANEL.LABELS.CONVERSATION.ADD_BUTTON')"
          @click="toggleLabels"
        >
          <span class="i-hugeicons-add-01 size-4 shrink-0" aria-hidden="true" />
          <span v-if="!sortedActiveLabels.length">
            {{ $t('CONTACT_PANEL.LABELS.CONVERSATION.ADD_BUTTON') }}
          </span>
        </button>

        <div
          :class="{
            'block visible': showSearchDropdownLabel,
            'hidden invisible': !showSearchDropdownLabel,
          }"
          class="absolute top-7 z-[9999] box-border w-full max-w-[22rem] overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-xl dark:border-n-strong"
        >
          <LabelDropdown
            v-if="showSearchDropdownLabel"
            :account-labels="accountLabels"
            :selected-labels="savedLabels"
            :allow-creation="isAdmin"
            @add="addLabelToConversation"
            @remove="removeLabelFromConversation"
            @create="openCreateLabelModal"
          />
        </div>
      </div>
    </div>
    <Spinner v-else />

    <Teleport to="body">
      <woot-modal
        v-model:show="showCreateLabelModal"
        :on-close="closeCreateLabelModal"
      >
        <AddLabelForm
          :prefill-title="newLabelTitle"
          @close="closeCreateLabelModal"
        />
      </woot-modal>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
  }
}
</style>
